import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines, Reveal } from "@/components/Motion";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const COVERS = ["/img/summit.jpg", "/img/gala.jpg", "/img/roundtable.jpg", "/img/neon.jpg", "/img/handshake.jpg", "/img/hero.jpg"];
const cover = (n, i) => n.cover_image || COVERS[i % COVERS.length];

export const NewsCard = ({ n, i = 0, big = false }) => (
  <Reveal delay={(i % 3) * 0.06} className={big ? "lg:col-span-2" : ""}>
    <Link to={`/news/${n.slug}`} className="group block h-full" data-testid={`news-card-${n.slug}`}>
      <div className={`overflow-hidden rounded-2xl border border-[#2A1240]/10 ${big ? "aspect-[16/8]" : "aspect-[16/10]"}`}>
        <img src={cover(n, i)} alt={n.title} className="h-full w-full object-cover opacity-75 group-hover:scale-105 transition-transform duration-[1400ms]" />
      </div>
      <p className="mt-5 text-[11px] uppercase tracking-[0.24em] text-[#8E2BC4]">{n.category} · {fmtDate(n.published_at)}</p>
      <h3 className={`mt-2 font-display text-[#2A1240] group-hover:text-[#8E2BC4] transition-colors ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{n.title}</h3>
      {n.excerpt && <p className="mt-2 text-sm text-slate-300 line-clamp-2">{n.excerpt}</p>}
    </Link>
  </Reveal>
);

export default function News() {
  useSeo("Balqis News: Partnership and Sponsorship Insights", "News, trends and case studies on brand partnerships, sponsorships, collaborations and marketing deals.", "/news");
  const [cat, setCat] = useState("");
  const [data, setData] = useState({ categories: [], items: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/news", { params: { category: cat } })
      .then((r) => setData({ categories: r.data?.categories || [], items: Array.isArray(r.data?.items) ? r.data.items : [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cat]);
  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="news-page">
      <Nav />
      <section className="pt-32 pb-10 mx-auto max-w-7xl px-5 sm:px-8">
        <p className="kicker">Balqis News</p>
        <MaskedLines as="h1" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight" lines={["The partnerships", <em key="n" className="italic gold-text">dispatch.</em>]} />
        <div className="mt-10 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="news-categories">
          {["", ...data.categories].map((c) => (
            <button key={c || "all"} onClick={() => setCat(c)} data-testid={`news-cat-${(c || "all").toLowerCase().replace(/\W+/g, "-")}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm transition-colors ${cat === c ? "bg-[#2A1240] text-white font-semibold" : "border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]"}`}>{c || "All"}</button>
          ))}
        </div>
      </section>
      <section className="pb-24 mx-auto max-w-7xl px-5 sm:px-8">
        {!loading && !data.items.length && <div className="rounded-2xl border border-dashed border-[#2A1240]/15 p-12 text-center text-slate-300" data-testid="news-empty">No stories in this category yet — check back soon.</div>}
        <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((n, i) => <NewsCard key={n.id} n={n} i={i} big={i === 0 && !cat} />)}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function NewsArticle() {
  const { slug } = useParams();
  const [n, setN] = useState(null);
  const [err, setErr] = useState("");
  useSeo(n?.title || "Balqis News", n?.excerpt || "Partnership and sponsorship news from Balqis Book.", `/news/${slug}`);
  useEffect(() => { api.get(`/news/${slug}`).then((r) => setN(r.data)).catch((e) => setErr(errMsg(e))); }, [slug]);
  if (err) return <div className="min-h-screen bg-[#FFF7FB]"><Nav /><p className="pt-40 text-center text-slate-300" data-testid="news-error">{err}</p></div>;
  if (!n) return <div className="min-h-screen bg-[#FFF7FB]"><Nav /><p className="pt-40 text-center text-slate-400">Loading…</p></div>;
  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="news-article-page">
      <Nav />
      <article className="pt-32 pb-20 mx-auto max-w-3xl px-5 sm:px-8">
        <Link to="/news" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#2A1240]" data-testid="news-back-link"><ArrowLeft className="h-4 w-4" />Balqis News</Link>
        <p className="mt-10 text-[11px] uppercase tracking-[0.24em] text-[#8E2BC4]">{n.category} · {fmtDate(n.published_at)} · {n.author}</p>
        <MaskedLines as="h1" testid="news-title" className="mt-4 font-display text-4xl sm:text-5xl text-[#2A1240] tracking-tight" lines={[n.title]} />
        {n.excerpt && <p className="mt-6 text-lg text-slate-300">{n.excerpt}</p>}
        <div className="mt-10 overflow-hidden rounded-2xl border border-[#2A1240]/10 aspect-[16/9]"><img src={cover(n, 0)} alt="" className="h-full w-full object-cover opacity-80" /></div>
        <div className="mt-10 space-y-6 text-base sm:text-lg leading-relaxed text-slate-200" data-testid="news-body">
          {(n.body || "").split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </article>
      {n.related?.length > 0 && (
        <section className="pb-24 mx-auto max-w-7xl px-5 sm:px-8 border-t border-[#2A1240]/10 pt-16">
          <p className="kicker flex items-center gap-2">More in {n.category}<ArrowUpRight className="h-3.5 w-3.5" /></p>
          <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">{n.related.map((r, i) => <NewsCard key={r.id} n={r} i={i + 1} />)}</div>
        </section>
      )}
      <Footer />
    </div>
  );
}
